// @flow
import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import { throttle } from "lodash";
import { trimLeft } from "voca";
import cx from "classnames";
import t from "common/i18n";
import bindActions, { types as ats } from "../../actions";
import { selectAdvancedOptions } from "../../selectors";
import TaggedInput, { SUGGEST_CONTAINER_STATE } from "../TaggedInput";
import UploadPresetInput from "../UploadPresetInput";
import { getComponentConsumer } from "../UploadContainer/uploaderContext";

import "./AdvancedOptions.scss";

type Props = {
	setPublicId: ?string,
	setTags: string[],
	useTagsCallback: boolean,
	useUploadPresetsCallback: boolean,
	suggestedTags: string[],
	suggestedUploadPresets: string[],
	tagsInputValue: string,
	uploader: Object,
	publicIdError: boolean,
	[string]: Function,
	setUploadPreset: ?string,
};

type State = {
	suggestionsOpen: boolean,
};

export class AdvancedOptions extends Component<Props, State> {
	state = {
		suggestionsOpen: false,
	};

	componentDidMount() {
		const { uploader, useUploadPresetsCallback } = this.props;
		if (useUploadPresetsCallback) {
			uploader.retrieveClientUploadPresets();
		}
	}

	componentDidUpdate() {
		if (this.props.publicIdError && this.idInputRef.current) {
			this.idInputRef.current.focus();
		}
	}

	onPublicIdChange = (e: { target: { value: string } }) => {
		if (e.target) {
			this.props[ats.SET_PUBLIC_ID]({ value: trimLeft(e.target.value) });
		}
	};

	onTagsChange = (tags: string[]) => {
		this.props[ats.SET_UPLOAD_TAGS]({ tags });
	};

	onUploadPresetChange = (uploadPreset: string[]) => {
		this.props[ats.SET_UPLOAD_PRESET]({ uploadPreset });
	};

	onKeyUp = (e: KeyboardEvent) => {
		if (e.which === 27 || e.key === "Escape") {
			e.stopPropagation(); // dont propagate the esc so the widget stays open
		}
	};

	removeTagHandler = (tagIndx: number) => {
		this.props[ats.REMOVE_UPLOAD_TAG]({ index: tagIndx });
	};

	removeLastTagHandler = () => {
		this.props[ats.REMOVE_LAST_UPLOAD_TAG]();
	};

	filterSuggestions = throttle((prefix: string) => {
		if (this.props.useTagsCallback) {
			this.props.uploader.retrieveClientTags(prefix);
		}
	}, 100);

	filterPresetSuggestions = (prefix: string) => {
		if (this.props.suggestedUploadPresets) {
			this.props.uploader.retrieveClientUploadPresets(prefix);
		}
	};

	tagsInputChangeHandler = (value: string) => {
		this.props[ats.SET_TAGS_INPUT_VALUE]({ value });
	};

	reportSuggestionsContainerState = (value: string) => {
		const newState = (value === SUGGEST_CONTAINER_STATE.OPEN);
		const isChanged = this.state.suggestionsOpen !== newState;

		if (isChanged) {
			this.setState({
				suggestionsOpen: newState,
			});
		}
	};

	idInputRef = createRef<Object>();

	render() {
		const { props } = this;
		const errorableBorder = {
			"bc-window-border": !props.publicIdError,
			"bc-error": props.publicIdError,
		};

		//first item in suggestedUploadPresets is always ''
		const isUploadPresetInputDisplay = props.useUploadPresetsCallback && props.suggestedUploadPresets?.length > 1;

		return <div className={cx("slide bt", errorableBorder)}
			onKeyUp={this.onKeyUp}>
			<div className="field id">
				<input type="text" data-test="public-id" ref={this.idInputRef}
					   placeholder={t("advanced_options.public_id_ph")}
					   className={cx(errorableBorder)}
					   value={props.setPublicId || ""}
					   onChange={throttle(this.onPublicIdChange, 100)}/>
			</div>
			<div className={cx("field tag",
				{ "tags-suggestions-open": this.state.suggestionsOpen })}>
				<TaggedInput className="tagsinput"
							 id="adv-tag-select"
							 placeholder={t("advanced_options.tags_ph")}
							 values={props.setTags}
							 suggestions={props.suggestedTags}
							 changeHandler={this.onTagsChange}
							 inputChangeHandler={this.tagsInputChangeHandler}
							 removeTag={this.removeTagHandler}
							 removeLastTag={this.removeLastTagHandler}
							 filterSuggestions={this.filterSuggestions}
							 inputValue={props.tagsInputValue}
							 reportSuggestionsContainerState={this.reportSuggestionsContainerState}/>
			</div>
			{isUploadPresetInputDisplay && (
				<div className={cx("field upload-preset")}>
					<UploadPresetInput
						options={props.suggestedUploadPresets}
						onChange={this.onUploadPresetChange}
						value={props.setUploadPreset}
						filterSuggestions={this.filterPresetSuggestions}
					/>
				</div>
			)}
		</div>;
	}
}

export default connect<Props, *, *, *, *, *>(
	selectAdvancedOptions,
	bindActions,
)(getComponentConsumer(AdvancedOptions));
